import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  PencilIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  type Column,
  PlusIcon,
} from "@fawkes/ui";

import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { Tabs } from "@fawkes/ui";
import { speciesIconSrc } from "../components/AnimalOverview";
import {
  CatalogusLijst, SoortTegels, WeergaveKeuze, soortTegelsUitRijen,
  type Weergave,
} from "../components/CatalogusLijst";
import { leesWeergave, bewaarWeergave } from "../components/CatalogusKop";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21): the Crematorium tariff settings — the React port of the Jmix
// `CrematoriumTariefView`. Two stacked sections, each a grid + Add/Edit dialog:
//   • Gewichtsklassen — weight bands (Naam / Van kg / Tot kg / Volgorde / Actief).
//   • Tarieven — price per weight class × processing form with an effective date (tariff history is
//     kept by adding a new row with a later ingangsdatum, exactly like the Jmix view).
// The Jmix view stays reachable by its @Route until parity is signed off (strangler-fig).
//
// All endpoints use RELATIVE urls (the dataProvider prepends /api once; a leading slash doubles to
// /api/api → 404). ROLE_CREMATORIUM_TARIEF_ADMIN is server-enforced — there is no client signal, so
// affordances stay enabled and a 403 surfaces inline.

// Processing-form vocabulary — stable enum ids, rendered via i18n (crematoriumTarief.vorm.<ID>).
const VORMEN = [
  "INDIVIDUEEL",
  "UITGEBREID",
  "COLLECTIEF",
  "RESOMEREN_INDIVIDUEEL",
  "RESOMEREN_COLLECTIEF",
  "BEGRAFENIS_INDIVIDUEEL",
  "BEGRAFENIS_ANONIEM",
  "DESTRUCTIE",
] as const;

interface GewichtsklasseRow {
  id: string;
  version: number;
  naam: string;
  vanKg: number | null;
  totKg: number | null;
  volgorde: number | null;
  actief: boolean;
  diersoort?: string | null;
}
/** Het advies van de server bij het aanpassen van een gewichtsklasse. */
interface Bevinding {
  soort: "OVERLAP" | "GAT" | "NAAM" | "ONGELDIG";
  klasseId: string | null;
  klasseNaam: string | null;
  uitleg: string;
}
interface Wijziging {
  klasseId: string | null;
  huidigeNaam: string | null;
  nieuweNaam: string;
  vanKg: number | null;
  totKg: number | null;
  voorstelZelf: boolean;
}
interface Advies {
  bevindingen: Bevinding[];
  wijzigingen: Wijziging[];
  teVeelAanpassingen: boolean;
  samenvatting: string;
}

interface ToeslagRow {
  id: string;
  version: number;
  naam: string;
  verwerkingsvorm: string | null;
  gewichtsklasseId: string | null;
  gewichtsklasseNaam: string | null;
  bedrag: number;
  btwPercentage: number | null;
  geldigVanaf: string;
  geldigTot: string | null;
  automatischOpFactuur: boolean;
  actief: boolean;
  status: "ACTIEF" | "GEPLAND" | "VERLOPEN" | "INACTIEF";
}
type TabKey = "tarieven" | "klassen" | "toeslagen";
interface TariefRow {
  id: string;
  version: number;
  gewichtsklasseId: string | null;
  gewichtsklasseNaam: string | null;
  verwerkingsvorm: string;
  prijs: number | null;
  btwPercentage: number | null;
  ingangsdatum: string | null;
  einddatum: string | null;
  actief: boolean;
}

type SaveNotice = "conflict" | "forbidden" | "error" | null;
const statusToErr = (err: unknown): SaveNotice => {
  const code = (err as { statusCode?: number }).statusCode;
  if (code === 409) return "conflict";
  if (code === 403) return "forbidden";
  return "error";
};
const num = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// ── the page ─────────────────────────────────────────────────────────────────────────────────────
export function CrematoriumTariefOverview() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const klassen = useCustom<GewichtsklasseRow[]>({ url: "crematorium/gewichtsklassen", method: "get" });
  const tarieven = useCustom<TariefRow[]>({ url: "crematorium/tarieven", method: "get" });

  const klasseRows = klassen.data?.data ?? [];
  const tariefRows = tarieven.data?.data ?? [];

  const [klasseEdit, setKlasseEdit] = useState<GewichtsklasseRow | "new" | null>(null);
  const [tariefEdit, setTariefEdit] = useState<TariefRow | "new" | null>(null);
  const [vorm, setVorm] = useState("");
  const toeslagen = useCustom<ToeslagRow[]>({ url: "crematorium/toeslagen", method: "get" });
  const toeslagRows = toeslagen.data?.data ?? [];
  const [toeslagEdit, setToeslagEdit] = useState<ToeslagRow | "new" | null>(null);
  const [tab, setTab] = useState<TabKey>("tarieven");
  const [diersoort, setDiersoort] = useState("");
  const [geldigOp, setGeldigOp] = useState("");
  const [alleenActief, setAlleenActief] = useState(true);
  const [weergave, setWeergave] = useState<Weergave>(() => leesWeergave("fawkes.crematoriumTarief"));
  const kiesWeergave = (w: Weergave) => {
    setWeergave(w);
    bewaarWeergave("fawkes.crematoriumTarief", w);
  };

  const klasseNaam = (id: string | null): string =>
    klasseRows.find((k) => k.id === id)?.naam ?? "—";

  const klasseColumns: Column<GewichtsklasseRow>[] = [
    { header: t("crematoriumTarief.klasse.col.naam"), cell: (r) => r.naam },
    { header: t("crematoriumTarief.klasse.col.diersoort"), cell: (r) => r.diersoort || t("crematoriumTarief.diersoort.alle") },
    {
      header: t("crematoriumTarief.klasse.col.vanKg"),
      align: "right",
      cell: (r) => (r.vanKg != null ? `${r.vanKg}` : "—"),
    },
    {
      header: t("crematoriumTarief.klasse.col.totKg"),
      align: "right",
      cell: (r) => (r.totKg != null ? `${r.totKg}` : "—"),
    },
    {
      header: t("crematoriumTarief.klasse.col.volgorde"),
      align: "right",
      cell: (r) => (r.volgorde != null ? `${r.volgorde}` : "—"),
    },
    {
      header: t("crematoriumTarief.col.actief"),
      cell: (r) => (
        <StatusBadge tone={r.actief ? "success" : "neutral"}>
          {t(r.actief ? "crematoriumTarief.actief.ja" : "crematoriumTarief.actief.nee")}
        </StatusBadge>
      ),
    },
    {
      header: t("crematoriumTarief.col.acties"),
      cell: (r) => (
        <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setKlasseEdit(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
      ),
    },
  ];

  const tariefColumns: Column<TariefRow>[] = [
    { header: t("crematoriumTarief.tarief.col.klasse"), cell: (r) => r.gewichtsklasseNaam ?? klasseNaam(r.gewichtsklasseId) },
    { header: t("crematoriumTarief.tarief.col.vorm"), cell: (r) => t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) },
    {
      header: t("crematoriumTarief.tarief.col.prijs"),
      align: "right",
      cell: (r) => (r.prijs != null ? formatEuro(r.prijs, locale) : "—"),
    },
    {
      header: t("crematoriumTarief.tarief.col.btw"),
      align: "right",
      cell: (r) => (r.btwPercentage != null ? `${r.btwPercentage}%` : "—"),
    },
    {
      header: t("crematoriumTarief.tarief.col.ingangsdatum"),
      cell: (r) => (r.ingangsdatum ? formatDate(r.ingangsdatum, locale) : "—"),
    },
    {
      header: t("crematoriumTarief.tarief.col.einddatum"),
      cell: (r) => (r.einddatum ? formatDate(r.einddatum, locale) : "—"),
    },
    {
      header: t("crematoriumTarief.col.actief"),
      cell: (r) => (
        <StatusBadge tone={r.actief ? "success" : "neutral"}>
          {t(r.actief ? "crematoriumTarief.actief.ja" : "crematoriumTarief.actief.nee")}
        </StatusBadge>
      ),
    },
    {
      header: t("crematoriumTarief.col.acties"),
      cell: (r) => (
        <button
            type="button"
            className="tarief-rijactie"
            onClick={() => setTariefEdit(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
      ),
    },
  ];

  // De diersoort van een tarief volgt uit zijn gewichtsklasse; een klasse zonder soort geldt voor elke
  // soort en telt onder "Alle soorten".
  const diersoortVan = (r: TariefRow) =>
    klasseRows.find((k) => k.id === r.gewichtsklasseId)?.diersoort || t("crematoriumTarief.diersoort.alle");
  const soortTegels = useMemo(
    () => soortTegelsUitRijen(tariefRows, diersoortVan, t("dierAanwezig.stat.total"),
      (naam) => <img src={speciesIconSrc(naam)} alt="" width={28} height={28} />),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tariefRows, klasseRows, t],
  );
  const zichtbareTarieven = useMemo(() => tariefRows.filter((r) => {
    if (diersoort && diersoortVan(r) !== diersoort) return false;
    if (vorm && r.verwerkingsvorm !== vorm) return false;
    if (alleenActief && !r.actief) return false;
    if (geldigOp) {
      if (r.ingangsdatum && r.ingangsdatum > geldigOp) return false;
      if (r.einddatum && r.einddatum < geldigOp) return false;
    }
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tariefRows, klasseRows, diersoort, vorm, alleenActief, geldigOp, t]);
  // De kop: welke versie geldt (de laatste ingangsdatum tot vandaag) en hoeveel er staat.
  const vandaag = todayIso();
  const actieveVersie = useMemo(() => tariefRows
    .map((r) => r.ingangsdatum).filter((d): d is string => !!d && d <= vandaag).sort().reverse()[0] ?? null,
    [tariefRows, vandaag]);
  const wisFilters = () => { setDiersoort(""); setVorm(""); setGeldigOp(""); setAlleenActief(true); };

  const toeslagColumns: Column<ToeslagRow>[] = [
    { header: t("crematoriumTarief.toeslag.col.naam"), cell: (r) => r.naam },
    { header: t("crematoriumTarief.tarief.col.vorm"), cell: (r) => (r.verwerkingsvorm ? t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) : t("crematoriumTarief.toeslag.alleVormen")) },
    {
      header: t("crematoriumTarief.tarief.col.klasse"),
      cell: (r) => r.gewichtsklasseNaam ?? (r.gewichtsklasseId ? klasseNaam(r.gewichtsklasseId) : t("crematoriumTarief.toeslag.alleKlassen")),
    },
    { header: t("crematoriumTarief.toeslag.col.bedrag"), align: "right", cell: (r) => formatEuro(r.bedrag, locale) },
    { header: t("crematoriumTarief.tarief.col.btw"), align: "right", cell: (r) => (r.btwPercentage != null ? `${r.btwPercentage}%` : "—") },
    { header: t("crematoriumTarief.toeslag.col.geldigVanaf"), cell: (r) => formatDate(r.geldigVanaf, locale) },
    { header: t("crematoriumTarief.toeslag.col.geldigTot"), cell: (r) => (r.geldigTot ? formatDate(r.geldigTot, locale) : "—") },
    { header: t("crematoriumTarief.toeslag.col.automatisch"), cell: (r) => (r.automatischOpFactuur ? t("common.yes") : t("common.no")) },
    {
      header: t("crematoriumTarief.col.actief"),
      cell: (r) => (
        <StatusBadge tone={r.status === "ACTIEF" ? "success" : r.status === "GEPLAND" ? "info" : "neutral"}>
          {t(`crematoriumTarief.toeslag.status.${r.status}`)}
        </StatusBadge>
      ),
    },
    {
      header: t("crematoriumTarief.col.acties"),
      cell: (r) => (
        <button type="button" className="tarief-rijactie" onClick={() => setToeslagEdit(r)} title={t("common.edit")} aria-label={t("common.edit")}>
          <PencilIcon width={16} height={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="crem-page">
      {/* Kop: titel, acties en de cijfers van de geldende versie — de mockup. */}
      <div className="crem-page__head">
        <div className="crem-page__intro">
          <div>
            <h2 className="crem-page__title">{t("menu.crematieTarieven")}</h2>
            <p className="crem-page__sub">{t("crematoriumTarief.sub")}</p>
          </div>
          <div className="crem-page__actions">
            <Button variant="secondary" onClick={() => { klassen.refetch(); tarieven.refetch(); toeslagen.refetch(); }}>{t("crematoriumTarief.action.refresh")}</Button>
            <Button onClick={() => setTariefEdit("new")} disabled={klasseRows.length === 0}>
              <PlusIcon width={16} height={16} /> {t("crematoriumTarief.tarief.add")}
            </Button>
          </div>
        </div>
      </div>
      <div className="crem-strip">
        <div className="crem-stat crem-stat--accent"><span className="crem-stat__label">{t("crematoriumTarief.kpi.versie")}</span><span className="crem-stat__value">{actieveVersie ? formatDate(actieveVersie, locale) : "—"}</span></div>
        <div className="crem-stat"><span className="crem-stat__label">{t("crematoriumTarief.kpi.klassen")}</span><span className="crem-stat__value">{klasseRows.length}</span></div>
        <div className="crem-stat"><span className="crem-stat__label">{t("crematoriumTarief.kpi.regels")}</span><span className="crem-stat__value">{tariefRows.length}</span></div>
        <div className="crem-stat"><span className="crem-stat__label">{t("crematoriumTarief.kpi.toeslagen")}</span><span className="crem-stat__value">{toeslagRows.filter((x) => x.status === "ACTIEF").length}</span></div>
      </div>
      <Tabs
        tabs={[
          { key: "tarieven", label: t("crematoriumTarief.tab.tarieven") },
          { key: "klassen", label: t("crematoriumTarief.klasse.title") },
          { key: "toeslagen", label: t("crematoriumTarief.toeslag.title") },
        ]}
        activeKey={tab}
        onChange={(k) => setTab(k as TabKey)}
        ariaLabel={t("menu.crematieTarieven")}
      />

      {tab === "klassen" && (<>
      {/* Gewichtsklassen */}
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <span className="fawkes-muted">{t("crematoriumTarief.klasse.uitleg")}</span>
          <span className="asiel-tarief__toolbar-spacer" />
          <Button onClick={() => setKlasseEdit("new")}>
            <PlusIcon width={16} height={16} /> {t("crematoriumTarief.klasse.add")}
          </Button>
        </div>
        {klassen.isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crematoriumTarief.load.error")}
          </div>
        )}
        <IndeelbareTabel<GewichtsklasseRow> tabelKey="crematorium-tarief-overview-gewichtsklasse-row"
          columns={klasseColumns}
          rows={klasseRows}
          getRowKey={(r) => r.id}
          empty={klassen.isLoading ? t("common.loading") : t("crematoriumTarief.klasse.empty")}
        />
      </div>
      </>)}

      {tab === "tarieven" && (
      <div className="asiel-tarief__panel">
        {soortTegels.length > 0 && (
          <SoortTegels label={t("crematoriumTarief.klasse.col.diersoort")} gekozen={diersoort} onKies={setDiersoort} tegels={soortTegels} />
        )}
        <div className="asiel-tarief__toolbar">
          <SelectField
            label={t("crematoriumTarief.tarief.col.vorm")}
            value={vorm}
            onChange={(e) => setVorm(e.target.value)}
            options={[{ value: "", label: t("crematoriumTarief.toeslag.alleVormen") }, ...VORMEN.map((vm) => ({ value: vm, label: t(`crematoriumTarief.vorm.${vm}`) }))]}
          />
          <Field label={t("crematoriumTarief.filter.geldigOp")} type="date" value={geldigOp} onChange={(e) => setGeldigOp(e.target.value)} />
          <CheckboxField label={t("crematoriumTarief.filter.alleenActief")} checked={alleenActief} onChange={(e) => setAlleenActief(e.target.checked)} />
          <Button variant="secondary" onClick={wisFilters}>{t("crematoriumTarief.filter.wissen")}</Button>
        </div>
        {tarieven.isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crematoriumTarief.load.error")}
          </div>
        )}
        <div className="fawkes-catalogus__lijstkop">
          <span className="fawkes-muted">
            {zichtbareTarieven.length}{" "}
            {t(zichtbareTarieven.length === 1 ? "dierAanwezig.results.foundOne" : "dierAanwezig.results.found")}
          </span>
          <WeergaveKeuze
            weergave={weergave}
            onKies={kiesWeergave}
            labels={{
              groep: t("catalogus.weergave"),
              kaart: t("catalogus.weergave.kaart"),
              compact: t("catalogus.weergave.compact"),
              lijst: t("catalogus.weergave.lijst"),
            }}
          />
        </div>
        <CatalogusLijst<TariefRow>
          columns={tariefColumns}
          rows={zichtbareTarieven}
          getRowKey={(r) => r.id}
          empty={tarieven.isLoading ? t("common.loading") : t("crematoriumTarief.tarief.empty")}
          weergave={weergave}
        />
        <p className="fawkes-muted">{t("crematoriumTarief.tarief.versieUitleg")}</p>
      </div>
      )}

      {tab === "toeslagen" && (
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <span className="fawkes-muted">{t("crematoriumTarief.toeslag.uitleg")}</span>
          <span className="asiel-tarief__toolbar-spacer" />
          <Button onClick={() => setToeslagEdit("new")}><PlusIcon width={16} height={16} /> {t("crematoriumTarief.toeslag.add")}</Button>
        </div>
        {toeslagen.isError && <div className="asiel-tarief__notice asiel-tarief__notice--error">{t("crematoriumTarief.load.error")}</div>}
        <IndeelbareTabel<ToeslagRow> tabelKey="crematorium-tarief-overview-toeslag-row"
          columns={toeslagColumns}
          rows={toeslagRows}
          getRowKey={(r) => r.id}
          empty={toeslagen.isLoading ? t("common.loading") : t("crematoriumTarief.toeslag.empty")}
        />
      </div>
      )}

      {toeslagEdit && (
        <ToeslagDialog
          row={toeslagEdit === "new" ? null : toeslagEdit}
          klassen={klasseRows}
          onClose={() => setToeslagEdit(null)}
          onSaved={() => { setToeslagEdit(null); toeslagen.refetch(); }}
        />
      )}
      {klasseEdit && (
        <GewichtsklasseDialog
          row={klasseEdit === "new" ? null : klasseEdit}
          alleKlassen={klasseRows}
          onAangepast={() => klassen.refetch()}
          onClose={() => setKlasseEdit(null)}
          onSaved={() => {
            setKlasseEdit(null);
            klassen.refetch();
          }}
        />
      )}

      {tariefEdit && (
        <TariefDialog
          row={tariefEdit === "new" ? null : tariefEdit}
          klassen={klasseRows}
          onClose={() => setTariefEdit(null)}
          onSaved={() => {
            setTariefEdit(null);
            tarieven.refetch();
          }}
        />
      )}
    </div>
  );
}

// ── Gewichtsklasse dialog ──────────────────────────────────────────────────────────────────────────
function GewichtsklasseDialog(props: {
  row: GewichtsklasseRow | null;
  /** De hele ladder, om het advies op de buren te kunnen toepassen. */
  alleKlassen: GewichtsklasseRow[];
  onClose: () => void;
  onSaved: () => void;
  /** Na het doorvoeren van het advies: de lijst opnieuw laden zonder de dialoog te sluiten. */
  onAangepast?: () => void;
}) {
  const { row, alleKlassen, onClose, onSaved, onAangepast } = props;
  const t = useTranslate();
  const [naam, setNaam] = useState(row?.naam ?? "");
  const [vanKg, setVanKg] = useState(row?.vanKg != null ? String(row.vanKg) : "");
  const [totKg, setTotKg] = useState(row?.totKg != null ? String(row.totKg) : "");
  const [actief, setActief] = useState(row?.actief ?? true);
  const [diersoort, setDiersoort] = useState(row?.diersoort ?? "");

  const [naamMissing, setNaamMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  // ── het advies: wat er nog meer moet gebeuren als je dit zo opslaat ──
  // De grenzen van de klassen vormen een ladder. Zet je er een tussen, dan klopt de buurman niet
  // meer en zijn naam ook niet. Dat rekent de server na; hier tonen we het en bieden we aan het
  // in één keer te laten kloppen.
  const [advies, setAdvies] = useState<Advies | null>(null);
  const [adviesBezig, setAdviesBezig] = useState(false);
  const { mutate: vraagAdvies } = useCustomMutation();
  const { mutate: pasAan } = useCustomMutation();

  const huidigeInvoer = () => ({
    naam: naam.trim(),
    vanKg: num(vanKg),
    totKg: num(totKg),
    diersoort: diersoort || null,
    actief,
  });

  const controleer = () => {
    setAdviesBezig(true);
    vraagAdvies(
      {
        url: row ? `crematorium/gewichtsklassen/advies?id=${row.id}` : "crematorium/gewichtsklassen/advies",
        method: "post",
        values: huidigeInvoer(),
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => { setAdvies(res.data as unknown as Advies); setAdviesBezig(false); },
        onError: () => { setAdvies(null); setAdviesBezig(false); },
      },
    );
  };

  /** De voorgestelde aanpassingen op de buren doorvoeren; de klasse zelf gaat via Opslaan. */
  const pasAdviesToe = () => {
    const buren = (advies?.wijzigingen ?? []).filter((w) => !w.voorstelZelf && w.klasseId);
    if (buren.length === 0) return;
    let open = buren.length;
    for (const w of buren) {
      const bestaand = alleKlassen.find((k) => k.id === w.klasseId);
      pasAan(
        {
          url: `crematorium/gewichtsklassen/${w.klasseId}`,
          method: "put",
          values: {
            naam: w.nieuweNaam,
            vanKg: w.vanKg,
            totKg: w.totKg,
            diersoort: bestaand?.diersoort ?? null,
            actief: bestaand?.actief ?? true,
            version: bestaand?.version,
          },
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: () => { open -= 1; if (open === 0) { setAdvies(null); onAangepast?.(); } },
          onError: (err) => { open -= 1; setNotice(statusToErr(err)); },
        },
      );
    }
  };

  const submit = () => {
    if (naam.trim() === "") {
      setNaamMissing(true);
      return;
    }
    setNaamMissing(false);
    setNotice(null);
    const values = {
      naam: naam.trim(),
      vanKg: num(vanKg),
      totKg: num(totKg),
      actief,
      diersoort: diersoort || null,
      version: row?.version,
    };
    mutate(
      {
        url: row ? `crematorium/gewichtsklassen/${row.id}` : "crematorium/gewichtsklassen",
        method: row ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={t(row ? "crematoriumTarief.klasse.edit.title" : "crematoriumTarief.klasse.add.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`crematoriumTarief.notice.${notice}`)}
          </div>
        )}
        <Field label={t("crematoriumTarief.klasse.col.naam")} value={naam} onChange={(e) => setNaam(e.target.value)} />
        {naamMissing && <div className="asiel-tarief__field-error">{t("crematoriumTarief.field.naam.required")}</div>}
        <Field
          label={t("crematoriumTarief.klasse.col.vanKg")}
          value={vanKg}
          inputMode="decimal"
          onChange={(e) => setVanKg(e.target.value)}
        />
        <Field
          label={t("crematoriumTarief.klasse.col.totKg")}
          value={totKg}
          inputMode="decimal"
          onChange={(e) => setTotKg(e.target.value)}
        />
        <SelectField
          label={t("crematoriumTarief.klasse.col.diersoort")}
          value={diersoort}
          onChange={(e) => setDiersoort(e.target.value)}
          options={[
            { value: "", label: t("crematoriumTarief.diersoort.alle") },
            { value: "Hond", label: "Hond" },
            { value: "Kat", label: "Kat" },
            { value: "Overig", label: "Overig" },
          ]}
        />
        <CheckboxField
          label={t("crematoriumTarief.col.actief")}
          checked={actief}
          onChange={(e) => setActief(e.target.checked)}
        />

        <div>
          <Button variant="secondary" onClick={controleer} disabled={adviesBezig}>
            {t("crematoriumTarief.klasse.controleer")}
          </Button>
        </div>
        {advies && (
          <div
            className={`asiel-tarief__notice${advies.teVeelAanpassingen ? " asiel-tarief__notice--error" : ""}`}
            data-testid="klasse-advies"
          >
            <p>{advies.samenvatting}</p>
            {advies.bevindingen.length > 0 && (
              <ul>
                {advies.bevindingen.map((b, i) => (
                  <li key={i}>{b.klasseNaam ? `${b.klasseNaam}: ${b.uitleg}` : b.uitleg}</li>
                ))}
              </ul>
            )}
            {advies.wijzigingen.filter((w) => !w.voorstelZelf).length > 0 && (
              <>
                <p className="fawkes-muted">{t("crematoriumTarief.klasse.advies.voorstel")}</p>
                <ul>
                  {advies.wijzigingen.filter((w) => !w.voorstelZelf).map((w) => (
                    <li key={w.klasseId}>
                      {w.huidigeNaam} → <strong>{w.nieuweNaam}</strong>
                      {` (${w.vanKg ?? 0} – ${w.totKg ?? "∞"} kg)`}
                    </li>
                  ))}
                </ul>
                {!advies.teVeelAanpassingen && (
                  <Button variant="secondary" onClick={pasAdviesToe}>
                    {t("crematoriumTarief.klasse.advies.toepassen")}
                  </Button>
                )}
              </>
            )}
            {advies.wijzigingen.some((w) => w.voorstelZelf) && (
              <p className="fawkes-muted">
                {t("crematoriumTarief.klasse.advies.eigenNaam", {
                  args: [advies.wijzigingen.find((w) => w.voorstelZelf)!.nieuweNaam],
                })}
                {" "}
                <button
                  type="button"
                  className="fawkes-link"
                  onClick={() => setNaam(advies.wijzigingen.find((w) => w.voorstelZelf)!.nieuweNaam)}
                >
                  {t("crematoriumTarief.klasse.advies.naamOvernemen")}
                </button>
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Tarief dialog ────────────────────────────────────────────────────────────────────────────────
function TariefDialog(props: {
  row: TariefRow | null;
  klassen: GewichtsklasseRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { row, klassen, onClose, onSaved } = props;
  const t = useTranslate();
  const [gewichtsklasseId, setGewichtsklasseId] = useState(
    row?.gewichtsklasseId ?? klassen[0]?.id ?? "",
  );
  const [verwerkingsvorm, setVerwerkingsvorm] = useState(row?.verwerkingsvorm ?? VORMEN[0]);
  const [prijs, setPrijs] = useState(row?.prijs != null ? String(row.prijs) : "");
  const [btw, setBtw] = useState(row?.btwPercentage != null ? String(row.btwPercentage) : "21");
  const [ingangsdatum, setIngangsdatum] = useState(row?.ingangsdatum ?? todayIso());
  const [einddatum, setEinddatum] = useState(row?.einddatum ?? "");
  const [actief, setActief] = useState(row?.actief ?? true);

  const [prijsMissing, setPrijsMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    const p = num(prijs);
    if (p == null) {
      setPrijsMissing(true);
      return;
    }
    setPrijsMissing(false);
    setNotice(null);
    const values = {
      gewichtsklasseId,
      verwerkingsvorm,
      prijs: p,
      btwPercentage: num(btw),
      ingangsdatum: ingangsdatum === "" ? null : ingangsdatum,
      einddatum: einddatum === "" ? null : einddatum,
      actief,
      version: row?.version,
    };
    mutate(
      {
        url: row ? `crematorium/tarieven/${row.id}` : "crematorium/tarieven",
        method: row ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  return (
    <Modal
      open
      title={t(row ? "crematoriumTarief.tarief.edit.title" : "crematoriumTarief.tarief.add.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`crematoriumTarief.notice.${notice}`)}
          </div>
        )}
        <SelectField
          label={t("crematoriumTarief.tarief.col.klasse")}
          value={gewichtsklasseId}
          onChange={(e) => setGewichtsklasseId(e.target.value)}
          options={klassen.map((k) => ({ value: k.id, label: k.naam }))}
        />
        <SelectField
          label={t("crematoriumTarief.tarief.col.vorm")}
          value={verwerkingsvorm}
          onChange={(e) => setVerwerkingsvorm(e.target.value)}
          options={VORMEN.map((v) => ({ value: v, label: t(`crematoriumTarief.vorm.${v}`) }))}
        />
        <Field
          label={t("crematoriumTarief.tarief.col.prijs")}
          value={prijs}
          inputMode="decimal"
          onChange={(e) => setPrijs(e.target.value)}
        />
        {prijsMissing && <div className="asiel-tarief__field-error">{t("crematoriumTarief.field.prijs.required")}</div>}
        <Field
          label={t("crematoriumTarief.tarief.col.btw")}
          value={btw}
          inputMode="decimal"
          onChange={(e) => setBtw(e.target.value)}
        />
        <Field
          label={t("crematoriumTarief.tarief.col.ingangsdatum")}
          type="date"
          value={ingangsdatum}
          onChange={(e) => setIngangsdatum(e.target.value)}
        />
        <Field
          label={t("crematoriumTarief.tarief.col.einddatum")}
          type="date"
          value={einddatum}
          onChange={(e) => setEinddatum(e.target.value)}
        />
        <CheckboxField
          label={t("crematoriumTarief.col.actief")}
          checked={actief}
          onChange={(e) => setActief(e.target.checked)}
        />
      </div>
    </Modal>
  );
}

/** Een toeslag maken of wijzigen. Een lopende toeslag wijzigen maakt op de server een nieuwe versie. */
function ToeslagDialog(props: {
  row: ToeslagRow | null;
  /** Om de toeslag aan een gewichtsklasse te kunnen hangen: 5 kg kost minder energie dan 20. */
  klassen: GewichtsklasseRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { row, klassen, onClose, onSaved } = props;
  const t = useTranslate();
  const [naam, setNaam] = useState(row?.naam ?? "");
  const [vorm, setVorm] = useState(row?.verwerkingsvorm ?? "");
  const [klasse, setKlasse] = useState(row?.gewichtsklasseId ?? "");
  const [bedrag, setBedrag] = useState(row?.bedrag != null ? String(row.bedrag) : "");
  const [btw, setBtw] = useState(row?.btwPercentage != null ? String(row.btwPercentage) : "9");
  const [geldigVanaf, setGeldigVanaf] = useState(row?.geldigVanaf ?? todayIso());
  const [geldigTot, setGeldigTot] = useState(row?.geldigTot ?? "");
  const [automatisch, setAutomatisch] = useState(row?.automatischOpFactuur ?? true);
  const [actief, setActief] = useState(row?.actief ?? true);
  const [fout, setFout] = useState<string | null>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    if (naam.trim() === "" || num(bedrag) == null) {
      setFout(t("crematoriumTarief.toeslag.verplicht"));
      return;
    }
    setFout(null);
    mutate(
      {
        url: row ? `crematorium/toeslagen/${row.id}` : "crematorium/toeslagen",
        method: row ? "put" : "post",
        values: {
          naam: naam.trim(),
          verwerkingsvorm: vorm || null,
          gewichtsklasseId: klasse || null,
          bedrag: num(bedrag),
          btwPercentage: num(btw),
          geldigVanaf: geldigVanaf || todayIso(),
          geldigTot: geldigTot || null,
          automatischOpFactuur: automatisch,
          actief,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setFout(t(`crematoriumTarief.notice.${statusToErr(err)}`)),
      },
    );
  };

  return (
    <Modal
      open
      title={t(row ? "crematoriumTarief.toeslag.edit.title" : "crematoriumTarief.toeslag.add.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {fout && <div className="asiel-tarief__notice asiel-tarief__notice--error">{fout}</div>}
        <Field label={t("crematoriumTarief.toeslag.col.naam")} value={naam} onChange={(e) => setNaam(e.target.value)} />
        <SelectField
          label={t("crematoriumTarief.tarief.col.vorm")}
          value={vorm}
          onChange={(e) => setVorm(e.target.value)}
          options={[{ value: "", label: t("crematoriumTarief.toeslag.alleVormen") }, ...VORMEN.map((vm) => ({ value: vm, label: t(`crematoriumTarief.vorm.${vm}`) }))]}
        />
        <SelectField
          label={t("crematoriumTarief.tarief.col.klasse")}
          value={klasse}
          onChange={(e) => setKlasse(e.target.value)}
          options={[
            { value: "", label: t("crematoriumTarief.toeslag.alleKlassen") },
            ...klassen.map((k) => ({ value: k.id, label: k.naam })),
          ]}
        />
        <Field label={t("crematoriumTarief.toeslag.col.bedrag")} value={bedrag} inputMode="decimal" onChange={(e) => setBedrag(e.target.value)} />
        <SelectField
          label={t("crematoriumTarief.tarief.col.btw")}
          value={btw}
          onChange={(e) => setBtw(e.target.value)}
          options={[{ value: "9", label: "9%" }, { value: "21", label: "21%" }]}
        />
        <Field label={t("crematoriumTarief.toeslag.col.geldigVanaf")} type="date" value={geldigVanaf} onChange={(e) => setGeldigVanaf(e.target.value)} />
        <Field label={t("crematoriumTarief.toeslag.col.geldigTot")} type="date" value={geldigTot} onChange={(e) => setGeldigTot(e.target.value)} />
        <p className="fawkes-muted">{t("crematoriumTarief.toeslag.geldigTotUitleg")}</p>
        <CheckboxField label={t("crematoriumTarief.toeslag.col.automatisch")} checked={automatisch} onChange={(e) => setAutomatisch(e.target.checked)} />
        <CheckboxField label={t("crematoriumTarief.col.actief")} checked={actief} onChange={(e) => setActief(e.target.checked)} />
      </div>
    </Modal>
  );
}
